import axios from "axios";
import { API_BASE_URL } from "../apiConstants";
import { getAuthHeader } from "./authHeader";
import { toCamel } from "../utils/caseConverter";
import { loadErrorMessage } from "../utils/loadError";

// The built-in coach (lib/nexus_dialer_web/controllers/coach_companion_controller.ex).
// Every figure in these responses is computed on the server from the rep's
// own calls, callbacks and deals; the client only lays them out.
const API_URL = `${API_BASE_URL}/coach`;

// The rep's own clock decides which day and week the coach is talking about.
const zone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
};

const fail = (error, context) => {
  const status = error.response?.status;
  const payload = error.response?.data;
  const message =
    status === 403
      ? payload?.error?.message ||
        payload?.error ||
        "You don't have access to this coach view."
      : status === 422 && typeof payload?.error === "string"
        ? payload.error
        : loadErrorMessage(error, context);
  const wrapped = new Error(
    typeof message === "string" ? message : `Could not load ${context}.`,
  );
  wrapped.status = status;
  wrapped.code = payload?.code || payload?.error?.code;
  throw wrapped;
};

const get = async (path, context, params = {}) => {
  try {
    const response = await axios.get(`${API_URL}${path}`, {
      ...getAuthHeader(),
      params,
    });
    return toCamel(response.data?.data ?? null);
  } catch (error) {
    return fail(error, context);
  }
};

const post = async (path, body, context) => {
  try {
    const response = await axios.post(
      `${API_URL}${path}`,
      body,
      getAuthHeader(),
    );
    return toCamel(response.data?.data ?? null);
  } catch (error) {
    return fail(error, context);
  }
};

const coachCompanionApi = {
  /** Brief, plan, recap, live progress, history, manager notes, attention. */
  getCompanion: () => get("/companion", "your coach", { tz: zone() }),
  /** Counts for the shell presence. */
  getPresence: () => get("/presence", "the coach", { tz: zone() }),
  /** Follow-up queue with the latest draft per contact and what can send. */
  getFollowups: () => get("/followups", "follow-ups", { tz: zone() }),
  /**
   * Draft one follow-up as an assistant action awaiting approval.
   * `{contactId, channel: "email" | "sms", body?, subject?, replacesActionId?}`
   */
  draftFollowup: ({ contactId, channel, body, subject, replacesActionId }) =>
    post(
      "/followups/drafts",
      {
        contact_id: contactId,
        channel,
        ...(body != null ? { body } : {}),
        ...(subject != null ? { subject } : {}),
        ...(replacesActionId ? { replaces_action_id: replacesActionId } : {}),
      },
      "the draft",
    ),
  /** Managers: every rep's focus, targets, progress and recap trend. */
  getTeamPlans: () => get("/team/plans", "team plans"),
  /** Managers: one rep's summary. */
  getAgentPlan: (userId) =>
    get(`/agents/${encodeURIComponent(userId)}/plan`, "this rep's plan"),
  /** Managers: `{kind: "note", body, week: "current" | "next"}` or `{kind: "focus", focusSkill}`. */
  addNote: (userId, { kind, body, focusSkill, week }) =>
    post(
      `/agents/${encodeURIComponent(userId)}/notes`,
      {
        kind,
        ...(body != null ? { body } : {}),
        ...(focusSkill ? { focus_skill: focusSkill } : {}),
        ...(week ? { week } : {}),
      },
      "the note",
    ),
};

export default coachCompanionApi;
